// OneDrive:IgnoreCodeCoverage

import { createPairedEvent, IPairedEventType, IPairedEvent } from '../PairedEventBase';
import { EventFieldType } from '../IEvent';
import { ResultTypeEnum } from './ResultTypeEnum';
import { IExtraData } from './IExtraData';
export { ResultTypeEnum };

/**
 * @public
 */
export interface IQosStartSchema {
    /**
     * This is the name of the qos event
     */
    name: string;
    /**
     * This is a startTime override for the qos event
     */
    startTimeOverride?: number;
    /**
     * This is result code for the qos event
     */
    resultCode?: string;
    /**
     * This is the result type enum
     */
    resultType?: ResultTypeEnum;
    /**
     * This is the error message to be logged with failed result
     */
    error?: string;
    /**
     * This is the random extra data for the qos event
     */
    extraData?: IExtraData;
    /**
     * Extra data for the qos event that should be logged as aggregatable metrics
     */
    extraMetrics?: IExtraData;
}

/**
 * @public
 */
export interface IQosEndSchema {
    /**
     * This is the name of the qos event
     */
    name?: string;
    /**
     * This is a startTime override for the qos event
     */
    startTimeOverride?: number;
    /**
     * This is result code for the qos event
     */
    resultCode?: string;
    /**
     * This is the result type enum
     */
    resultType: ResultTypeEnum;
    /**
     * This is the error message to be logged with failed result
     */
    error?: string;
    /**
     * This is the random extra data for the qos event
     */
    extraData?: IExtraData;
    /**
     * Extra data for the qos event that should be logged as aggregatable metrics
     */
    extraMetrics?: IExtraData;
}

/**
 * @public
 */
export interface IQos extends IPairedEvent<IQosStartSchema, IQosEndSchema> {
    data: IQosStartSchema & IQosEndSchema;
    /**
     * This writes verbose logs to memory to be uploaded if event ends with failure.
     */
    verbose(message: string): void;
    /**
     * Sets a time limit for the scenario. A timeout result will be logged if no end happens in desired timeframe.
     */
    setTimeout(ms: number, data: IQosEndSchema): void;

    /**
     * This will end the Qos event
     * @param data {IQosEndSchema}     * -optional startTimeOverride - This is a startTime override for the qos event
     * -optional resultCode - This is result code for the qos event
     * -required resultType - This is the result type enum
     * -optional error - This is the error message to be logged with failed result
     * -optional extraData - This is the random extra data for the qos event
     * -optional extraMetrics - Extra data for the qos event that should be logged as aggregatable metrics
     */
    end(data: IQosEndSchema): void;
}

/**
 * @public
 */
export type Qos = IQos;

/**
 * @public
 */
export const Qos: IQosType = createPairedEvent<IQosStartSchema, IQosEndSchema>({
    eventName: 'Qos,',
    shortEventName: 'Qos'
}, {
        name: {
            isKey: true,
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        startTimeOverride: {
            isPrefixingDisabled: true,
            type: EventFieldType.Number
        },
        resultCode: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        resultType: {
            typeRef: ResultTypeEnum,
            isPrefixingDisabled: true,
            type: EventFieldType.Enum
        },
        error: {
            isPrefixingDisabled: true,
            type: EventFieldType.String
        },
        extraData: EventFieldType.Object,
        extraMetrics: {
            isPrefixingDisabled: true,
            type: EventFieldType.Object
        }
    });

/**
 * @public
 */
export interface IQosType extends IPairedEventType<IQosStartSchema, IQosEndSchema> {
}
